import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Skeleton, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import { LOOSE_ENDS_KEY, fixLabel, groupByFlow, looseEndStep, looseEndText, looseEndsSummary, stepHref } from './loose-ends';
import './loose-ends.css';

const MUTED = 'var(--rr-color-text-muted)';

/** The loose ends in the business's switched-on flows, shared by every page that shows them (one request, cached 30 seconds). */
export function useLooseEnds(enabled = true) {
  return useQuery({ queryKey: LOOSE_ENDS_KEY, queryFn: () => flows.listLooseEnds(), select: (r) => r.data, staleTime: 30_000, enabled });
}

/**
 * The loose ends, grouped by flow (2 November 2026): what is wrong with each step in plain words, whether chats stop
 * there, a Fix when the service knows one, and Open the step in the builder. Fix all makes only the fixes that are
 * certain; a fix that changes what customers see today asks first.
 */
export function LooseEndsList({ onChanged, withTitle = true, guide }: {
  /** Called after a fix, so a page can refresh what it shows besides the flows. */
  onChanged?: () => void;
  withTitle?: boolean;
  /** `data-guide` for the help tour, on the copy in the Flows drawer. */
  guide?: string;
}) {
  const t = useT();
  const qc = useQueryClient();
  const query = useLooseEnds();
  const data = query.data;
  const groups = useMemo(() => groupByFlow(data?.items ?? []), [data]);

  const fix = useMutation({
    mutationFn: (req: { keys?: string[]; all?: boolean }) => flows.fixLooseEnds(req),
    onSuccess: (res) => {
      qc.setQueryData(LOOSE_ENDS_KEY, { data: res.data.loose_ends });
      // The flows a fix touched: the list, and any one open in a builder or a detail page.
      void qc.invalidateQueries({ queryKey: ['flows'], exact: true });
      void qc.invalidateQueries({ queryKey: ['flow'] });
      onChanged?.();
      notifySuccess(res.data.fixed > 0 ? t('flows.looseEnds.fixed', { count: formatCount(res.data.fixed) }) : t('flows.looseEnds.fixedNone'), t('flows.looseEnds.title'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('flows.looseEnds.fixFailed'))
  });

  const fixOne = (e: flows.LooseEnd) => {
    if (!e.fix) return;
    if (e.fix.sure) { fix.mutate({ keys: [e.key] }); return; }
    // Not certain: the step still works today (it opens an old version that is switched on), so the change is said first.
    const name = e.fix.to_name?.trim() || t('flows.looseEnds.anotherFlow');
    openConfirmModal({
      title: t('flows.looseEnds.confirm.title'),
      children: (
        <Text role="bodySmall">
          {e.fix.action === 'switch_off'
            ? t('flows.looseEnds.confirm.switchOff', { name })
            : t('flows.looseEnds.confirm.relink', { step: looseEndStep(t, e), name })}
        </Text>
      ),
      labels: { confirm: fixLabel(t, e.fix) ?? t('flows.looseEnds.fix.button'), cancel: t('action.cancel') },
      onConfirm: () => fix.mutate({ keys: [e.key] })
    });
  };

  const fixAll = () => openConfirmModal({
    title: t('flows.looseEnds.confirm.allTitle'),
    children: <Text role="bodySmall">{t('flows.looseEnds.confirm.all', { count: formatCount(data?.sure_fixes ?? 0) })}</Text>,
    labels: { confirm: t('flows.looseEnds.fixAllConfirm'), cancel: t('action.cancel') },
    onConfirm: () => fix.mutate({ all: true })
  });

  return (
    <Stack gap="md" data-testid="loose-ends" data-guide={guide}>
      {withTitle && (
        <Stack gap={2}>
          <Title order={4}>{t('flows.looseEnds.title')}</Title>
          <Text role="caption" c={MUTED}>{t('flows.looseEnds.intro')}</Text>
        </Stack>
      )}
      {query.isPending ? <Skeleton height={120} /> : query.error ? (
        <Alert color="error" role="alert">
          <Stack gap="xs">
            <Text role="bodySmall">{t('flows.looseEnds.loadError')}</Text>
            <Group><Button size="xs" variant="default" onClick={() => void query.refetch()}>{t('action.retry')}</Button></Group>
          </Stack>
        </Alert>
      ) : groups.length === 0 ? (
        // The sentence as the description: this sits inside a card or a drawer, where a headline-sized title shouts.
        <div data-testid="loose-ends-empty"><EmptyState description={t('flows.looseEnds.empty')} /></div>
      ) : (
        <>
          <Group justify="space-between" align="center" gap="sm" wrap="wrap">
            <Text role="bodySmall" data-testid="loose-ends-count">{looseEndsSummary(t, data!)}</Text>
            {data!.sure_fixes > 0 && (
              <Button size="xs" onClick={fixAll} loading={fix.isPending && fix.variables?.all === true} data-testid="loose-ends-fix-all">
                {t('flows.looseEnds.fixAll', { count: formatCount(data!.sure_fixes) })}
              </Button>
            )}
          </Group>
          {data!.sure_fixes > 0 && <Text role="caption" c={MUTED}>{t('flows.looseEnds.fixAllHint')}</Text>}
          {groups.map((g) => (
            <section key={g.flowId} className="rr-loose-ends__flow" data-testid="loose-ends-flow" aria-label={g.flowName}>
              <Text role="bodySmall" fw={700}>{g.flowName}</Text>
              <ul className="rr-loose-ends__items">
                {g.items.map((e) => {
                  const label = fixLabel(t, e.fix);
                  return (
                    <li key={e.key} className={`rr-loose-ends__item${e.breaks ? ' is-breaking' : ''}`} data-testid="loose-end" data-kind={e.kind}
                      data-breaks={e.breaks ? 'yes' : 'no'}>
                      <div className="rr-loose-ends__body">
                        <Group gap={6} wrap="wrap">
                          <Text role="bodySmall" fw={600}>{looseEndStep(t, e)}</Text>
                          {e.breaks && <Badge size="sm" variant="light" color="orange">{t('flows.looseEnds.breaks')}</Badge>}
                        </Group>
                        <Text role="caption">{looseEndText(t, e)}</Text>
                      </div>
                      <Group gap="xs" wrap="wrap" className="rr-loose-ends__actions">
                        {label && (
                          <Button size="xs" variant={e.fix!.sure ? 'filled' : 'default'} onClick={() => fixOne(e)} disabled={fix.isPending && fix.variables?.all === true}
                            loading={fix.isPending && fix.variables?.keys?.[0] === e.key} data-testid="loose-end-fix">
                            {label}
                          </Button>
                        )}
                        <Link to={stepHref(e)}>
                          <Button size="xs" variant="subtle" data-testid="loose-end-open">
                            {e.step_id && e.kind !== 'same_start' ? t('flows.looseEnds.openStep') : t('flows.looseEnds.openFlow')}
                          </Button>
                        </Link>
                      </Group>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </>
      )}
    </Stack>
  );
}
